import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { useSnackbar } from "notistack";

import type {
  DefaultExclusions,
  SiteId,
  OptinId,
  ListId,
  Optin,
  OptinResponse,
  CreateOptinVariables,
  UpdateOptinVariables,
  DeleteOptinVariables,
  OptinsResponse,
} from "../../types/optins";
import type { OptinTemplatesResponse } from "../../types/templates";
import { withJWTClient } from "../connectClient";

import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

const baseUrl = import.meta.env.VITE_OPTINS_API_URL as string;

// Query keys
export const optinsKeys = {
  all: (siteId: SiteId) => ["optins", siteId],
  detail: (siteId: SiteId, optinId: OptinId) => [
    ...optinsKeys.all(siteId),
    "detail",
    optinId,
  ],
  contacts: (siteId: SiteId, listId: ListId) => [
    ...optinsKeys.detail(siteId, listId),
    "contacts",
  ],
};

// API functions
const fetchAllOptins = async (siteId: SiteId): Promise<Optin[]> => {
  const { data } = await withJWTClient((client) =>
    client.get<OptinsResponse>(`${baseUrl}/${siteId}/config`),
  );
  return data?.optins ?? [];
};

export const fetchAllTemplates = async (siteId: SiteId): Promise<Optin[]> => {
  const { data } = await withJWTClient((client) =>
    client.get<OptinTemplatesResponse>(`${baseUrl}/${siteId}/templates`),
  );
  return data?.templates ?? [];
};

export const fetchOptin = async ({
  siteId,
  optinId,
}: {
  siteId: SiteId;
  optinId: OptinId;
}): Promise<Optin> => {
  const { data } = await withJWTClient((client) =>
    client.get<OptinResponse>(`${baseUrl}/${siteId}/config/${optinId}`),
  );
  return data.optin;
};

const createOptin = async ({
  siteId,
  optin,
}: {
  siteId: SiteId;
  optin: Pick<Optin, "name">;
}): Promise<Optin> => {
  const { data } = await withJWTClient((client) =>
    client.post<OptinResponse>(`${baseUrl}/${siteId}/config`, optin),
  );
  return data.optin;
};

const updateOptin = async ({
  siteId,
  data,
}: {
  siteId: SiteId;
  data: Partial<Optin> & { id: OptinId };
}): Promise<Optin> => {
  // `status` is derived by the API from `active` and `schedule`; it is not an
  // updateable field. Callers that spread a whole optin into an update would
  // otherwise send it straight back.
  const payload = { ...data };
  delete payload.status;

  const { data: responseData } = await withJWTClient((client) =>
    client.put<OptinResponse>(
      `${baseUrl}/${siteId}/config/${data.id}`,
      payload,
    ),
  );
  return responseData.optin;
};

const deleteOptin = async ({
  siteId,
  id,
}: {
  siteId: SiteId;
  id: OptinId;
}): Promise<void> => {
  await withJWTClient((client) =>
    client.delete(`${baseUrl}/${siteId}/config/${id}`),
  );
};

// React Query hooks
export const allOptinsQueryOptions = (siteId: SiteId) => ({
  queryKey: optinsKeys.all(siteId),
  queryFn: () => fetchAllOptins(siteId),
});

export function useOptins(
  siteId: SiteId,
  options?: Omit<UseQueryOptions<Optin[], Error>, "queryKey" | "queryFn">,
) {
  return useQuery({
    ...allOptinsQueryOptions(siteId),
    ...options,
  });
}

export function useOptin(siteId: SiteId, optinId: OptinId) {
  return useQuery({
    queryKey: optinsKeys.detail(siteId, optinId),
    queryFn: () => fetchOptin({ siteId, optinId }),
    enabled: !!optinId,
  });
}

export function useCreateOptin() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<Optin, Error, CreateOptinVariables>({
    mutationFn: ({ siteId, optin }) => createOptin({ siteId, optin }),
    onSuccess: () => {
      enqueueSnackbar("Exchange created successfully", { variant: "success" });
    },
    onSettled: (_, __, { siteId }) => {
      void queryClient.invalidateQueries({
        queryKey: optinsKeys.all(siteId),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to create exchange");
    },
  });
}

export function useUpdateOptin() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<
    Optin,
    Error,
    UpdateOptinVariables & { data: Partial<Optin> & { id: OptinId } }
  >({
    mutationFn: ({ siteId, data }) => updateOptin({ siteId, data }),
    onSettled: (_, __, { siteId }) => {
      void queryClient.invalidateQueries({
        queryKey: optinsKeys.all(siteId),
      });
    },
    onSuccess: () => {
      enqueueSnackbar("Exchange updated successfully", {
        variant: "success",
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to update exchange");
    },
  });
}

export function useDeleteOptin() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<void, Error, DeleteOptinVariables>({
    mutationFn: ({ siteId, id }) => deleteOptin({ siteId, id }),
    onSuccess: () => {
      enqueueSnackbar("Exchange deleted successfully", { variant: "success" });
    },
    onSettled: (_, __, { siteId }) => {
      void queryClient.invalidateQueries({
        queryKey: optinsKeys.all(siteId),
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to delete exchange");
    },
  });
}

export type DefaultExclusionsSettings = {
  /** Whether the site is in the default exclusions rollout. */
  enabled: boolean;
  /** What an exchange that never set its own toggles is excluded from. */
  defaults: DefaultExclusions;
};

const fetchDefaultExclusionsSettings = async (
  siteId: SiteId,
): Promise<DefaultExclusionsSettings> => {
  const { data } = await withJWTClient((client) =>
    client.get<DefaultExclusionsSettings>(
      `${baseUrl}/${siteId}/default-exclusions`,
    ),
  );
  return data;
};

/**
 * Whether default exclusions apply to this site, and their defaults. An API
 * that predates the endpoint answers with an error (the path falls through to
 * the per-exchange route), and any error reads as "not enabled": `retry: false`
 * and no data, so the section stays hidden.
 */
export function useDefaultExclusionsSettings(
  siteId: SiteId,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [...optinsKeys.all(siteId), "default-exclusions"],
    queryFn: () => fetchDefaultExclusionsSettings(siteId),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export const optinQueryOptions = (siteId: string, optinId: string) => {
  return {
    queryKey: optinsKeys.detail(siteId, optinId),
    queryFn: () => fetchOptin({ siteId, optinId }),
  };
};
